"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Search, Star } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { DealCard } from "@/components/deals"
import { DealGridSkeleton } from "@/components/skeletons"
import { useGetDealsQuery } from "@/store"
import { SEO } from "@/components/seo"


export default function DealsScreen() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedType, setSelectedType] = useState<string>("")

  // Fetch deals from API
  const { data: dealsData, isLoading } = useGetDealsQuery({
    ...(searchTerm && { search: searchTerm }),
    ...(selectedType && { dealType: selectedType as any }),
    isActive: true, // Only show active deals
  })
  const deals = dealsData?.data || []

  const dealTypes = [
    { value: "", label: "All Deals" },
    { value: "FIXED", label: "Fixed Deals" },
    { value: "CONFIGURABLE", label: "Configurable" },
    { value: "COMPLEX", label: "Premium Deals" },
  ]

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO 
        title="Cumbo Deals"
        description="Check out our latest special meal deals, family bundles, and limited-time offers at Zest and Zeal."
        canonical="/deals"
      />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold mb-2">Cumbo Deals</h1>
        <p className="text-muted-foreground">
          Discover our special meal deals and family packages
        </p>
      </motion.div>

      {/* Search and Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search deals..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            {dealTypes.map((type) => (
              <Button
                key={type.value}
                variant={selectedType === type.value ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedType(type.value)}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Featured Deals Section */}
      {deals.some(deal => deal.featured) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Star className="w-6 h-6 text-yellow-500 fill-current" />
            Featured Deals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deals
              .filter(deal => deal.featured)
              .map((deal, index) => (
                <DealCard key={deal.id} deal={deal} index={index} />
              ))}
          </div>
        </motion.div>
      )}

      {/* All Deals Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">
            {selectedType ? `${dealTypes.find(t => t.value === selectedType)?.label}` : "All Deals"}
          </h2>
          <Badge variant="secondary">
            {deals.length} deal{deals.length !== 1 ? "s" : ""}
          </Badge>
        </div>

        {isLoading ? (
          <DealGridSkeleton count={6} />
        ) : deals.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🍽️</div>
            <h3 className="text-xl font-medium mb-2">No deals found</h3>
            <p className="text-muted-foreground">
              {searchTerm || selectedType
                ? "Try adjusting your search or filters"
                : "Check back later for our special deals"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {deals.map((deal, index) => (
              <DealCard key={deal.id} deal={deal} index={index} />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  )
}